import React, { useEffect, useState } from "react";
import { getStorage } from "../utils/storage";
import { useNavigate } from "react-router-dom";
import { Page } from "react-onsenui";
import { axiosInstance as axios } from "../utils/axios";
import { getOrgIdsParam } from "../utils/orgSelection";
import { paths } from "../App";
import { Icon } from "@iconify/react";
import {
    Box,
    CircularProgress,
    InputAdornment,
    ListItemButton,
    Paper,
    TextField,
    Typography,
} from "@mui/material";
import { clPageStyle, clContentBox, clCardSx, clCardListSx, clCentredState, CL_PRIMARY, ClPageHeader, ClPageTitle } from "./cl-styles";

export const ChecklistMyChecklists = (props) => {

    const [authUser, setAuthUser] = useState(getStorage('u4'));

    /* Navigation */
    let navigate = useNavigate();
    const goBack = () => navigate(paths.checklist.dash, { replace: true });

    const [blueprints, setBlueprints] = useState(null);
    const [filteredBlueprints, setFilteredBlueprints] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [loading, setLoading] = useState(true);

    // Fetch blueprints with locations for user's organization
    const fetchBlueprints = async () => {
        if (!authUser || !authUser?.hotel_id) return;
        
        setLoading(true);
        try {
            const response = await axios.get('/checklist/blueprint/list', {
                params: { org_ids: getOrgIdsParam(authUser) }
            });
            
            if (response && response.data) {
                // Get user's desk viewing rights
                const allowedDeskIds = (authUser?.desks_ids || [])
                    .map(id => parseInt(id))
                    .filter(id => !isNaN(id));

                // If no viewing rights, stop here
                if (!allowedDeskIds.length) {
                    setBlueprints([]);
                    setFilteredBlueprints([]);
                    return;
                }
                
                // Filter blueprints by user's desk access (desk_id)
                const filteredBlueprints = response.data.filter(bp => {
                    // Check if blueprint's desk_id is within viewing rights
                    return bp.desk_id && allowedDeskIds.includes(parseInt(bp.desk_id));
                });
                
                // Expand blueprints with their locations
                const expanded = [];
                filteredBlueprints.forEach(bp => {
                    if (bp.locations && bp.locations.length > 0) {
                        bp.locations.forEach(loc => {
                            expanded.push({
                                blueprint: bp,
                                location: loc,
                                blueprintId: bp.id,
                                locationId: loc.id,
                                blueprintName: bp.name,
                                locationName: loc.location,
                                orgId: bp.org_id || authUser.hotel_id
                            });
                        });
                    } else {
                        // Blueprint without locations - still show it but without location
                        expanded.push({
                            blueprint: bp,
                            location: null,
                            blueprintId: bp.id,
                            locationId: null,
                            blueprintName: bp.name,
                            locationName: 'No location',
                            orgId: bp.org_id || authUser.hotel_id
                        });
                    }
                });
                
                setBlueprints(expanded);
                setFilteredBlueprints(expanded);
            }
        } catch (error) {
            console.error("Error fetching blueprints:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchBlueprints();
    }, [authUser]);

    // Filter blueprints based on search query
    useEffect(() => {
        if (!blueprints) return;
        
        if (!searchQuery.trim()) {
            setFilteredBlueprints(blueprints);
            return;
        }

        const query = searchQuery.toLowerCase().trim();
        const filtered = blueprints.filter(item => {
            const matchesBlueprint = item.blueprintName?.toLowerCase().includes(query);
            const matchesLocation = item.locationName?.toLowerCase().includes(query);
            return matchesBlueprint || matchesLocation;
        });
        
        setFilteredBlueprints(filtered);
    }, [searchQuery, blueprints]);

    const handleSelectLocation = (item) => {
        console.log("handleSelectLocation called with:", item);
        
        if (!item.locationId) {
            alert("This checklist template does not have a location assigned. Please contact your administrator.");
            return;
        }

        // Construct a URL hash similar to QR code format
        // The ChecklistSheet component expects a decodedText with a hash parameter
        const hashData = {
            id: item.blueprintId,
            geek: "@thinkdj",
            bpi: item.blueprintId,
            oi: item.orgId,
            loi: item.locationId,
            bpn: item.blueprintName,
            lon: item.locationName
        };

        let hashString = '';
        try {
            const serialized = JSON.stringify(hashData);
            hashString = window.btoa(unescape(encodeURIComponent(serialized)));
        } catch (error) {
            console.error("Error generating hash string:", error);
            alert("Unable to generate checklist link. Please contact your administrator.");
            return;
        }
        const decodedText = `https://atom.checkinn.co/r?hash=${hashString}`;

        console.log("Navigating with decodedText:", decodedText);

        // Navigate to ChecklistSheet with the data
        navigate(paths.checklist.details, {
            state: {
                data: {
                    decodedText: decodedText
                }
            }
        });
    };

    return (
        <Page renderToolbar={() => <ClPageHeader onBack={goBack} />} style={clPageStyle}>
            <Box sx={clContentBox}>
                <ClPageTitle title="My Checklists" subtitle="Select a location below to complete a checklist" />

                {/* Search */}
                <TextField
                    fullWidth
                    size="small"
                    variant="outlined"
                    placeholder="Search by template or location…"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    sx={{ mb: 1.5 }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <Icon icon="mdi:magnify" style={{ fontSize: 20, color: 'rgba(0,0,0,0.4)' }} />
                            </InputAdornment>
                        ),
                    }}
                />

                {/* Loading */}
                {loading && (
                    <Box sx={clCentredState}>
                        <CircularProgress size={36} sx={{ color: CL_PRIMARY }} />
                    </Box>
                )}

                {/* Empty state */}
                {!loading && filteredBlueprints.length === 0 && (
                    <Box sx={clCentredState}>
                        <Typography color="text.secondary" textAlign="center">
                            {searchQuery ? 'No checklists found matching your search' : 'No checklists available'}
                        </Typography>
                    </Box>
                )}

                {/* List */}
                {!loading && filteredBlueprints.length > 0 && (
                    <Box sx={clCardListSx}>
                        {filteredBlueprints.map((item, i) => (
                            <Paper key={`${item.blueprintId}-${item.locationId || 'no-loc'}-${i}`} elevation={0} sx={clCardSx}>
                                <ListItemButton
                                    disabled={!item.locationId}
                                    onClick={() => item.locationId && handleSelectLocation(item)}
                                    sx={{ px: 2, py: 1.25 }}
                                >
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        {/* Location name with icon inline */}
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.4 }}>
                                            <Icon
                                                icon={item.locationId ? 'mdi:map-marker' : 'mdi:map-marker-off'}
                                                style={{ fontSize: 15, color: item.locationId ? CL_PRIMARY : 'rgba(0,0,0,0.3)', flexShrink: 0 }}
                                            />
                                            <Typography variant="body2" fontWeight={700} sx={{ color: '#1a1a1a', lineHeight: 1.3 }}>
                                                {item.locationName || 'Unassigned location'}
                                            </Typography>
                                        </Box>
                                        {/* Template label */}
                                        <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 0.5 }}>
                                            <span style={{ fontWeight: 600, color: '#999', textTransform: 'uppercase', fontSize: '0.65rem', letterSpacing: '0.04em' }}>Template:</span>
                                            {' '}{item.blueprintName}
                                        </Typography>
                                        {/* Action hint */}
                                        {item.locationId
                                            ? <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <Icon icon="mdi:plus-circle-outline" style={{ fontSize: 14, color: CL_PRIMARY }} />
                                                <Typography variant="caption" sx={{ color: CL_PRIMARY, fontWeight: 600 }}>Tap to complete</Typography>
                                              </Box>
                                            : <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <Icon icon="mdi:map-marker-off-outline" style={{ fontSize: 14, color: 'rgba(0,0,0,0.38)' }} />
                                                <Typography variant="caption" sx={{ color: 'rgba(0,0,0,0.38)' }}>No location assigned</Typography>
                                              </Box>
                                        }
                                    </Box>
                                </ListItemButton>
                            </Paper>
                        ))}
                    </Box>
                )}
            </Box>
        </Page>
    );
};

